import React, { useState, useEffect } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {
    KeyboardDatePicker,
    KeyboardDatePickerProps,
    MuiPickersUtilsProvider,
} from '@material-ui/pickers';

export default function DatePickerInput({
    id,
    variant,
    label,
    value = '',
    ...rest
}: KeyboardDatePickerProps) {
    const [announcement, setAnnouncement] = useState('');
    const { onChange, ...restWithoutOnChange } = rest;

    // Apply WCAG heading hierarchy to DatePicker toolbar
    useEffect(() => {
        const applyWCAGHeadings = () => {
            // Find DatePicker dialogs
            const dialogs = document.querySelectorAll('[role="dialog"]');
            dialogs.forEach((dialog) => {
                // Find year title elements (should be h1 - main heading)
                const yearElements = dialog.querySelectorAll(
                    '.MuiPickersToolbar-yearTitle, .MuiPickersToolbar-yearSelection',
                );
                yearElements.forEach((element) => {
                    const htmlElement = element as HTMLElement;
                    htmlElement.setAttribute('role', 'heading');
                    htmlElement.setAttribute('aria-level', '1');
                    htmlElement.style.fontSize = '2rem'; // Larger for h1
                    htmlElement.style.fontWeight = '700'; // Bolder for h1
                    htmlElement.style.color = '#000000';
                });

                // Find month/date text elements (should be h2 - secondary heading)
                const monthElements = dialog.querySelectorAll(
                    '.MuiPickersToolbarText-toolbarTxt, .MuiPickersToolbarText-toolbarBtnSelected',
                );
                monthElements.forEach((element) => {
                    const htmlElement = element as HTMLElement;
                    htmlElement.setAttribute('role', 'heading');
                    htmlElement.setAttribute('aria-level', '2');
                    htmlElement.style.fontSize = '1.5rem'; // Smaller than h1
                    htmlElement.style.fontWeight = '600'; // Less bold than h1
                    htmlElement.style.color = '#000000';
                });
            });
        };

        // Apply when component mounts and when value changes
        applyWCAGHeadings();
        const observer = new MutationObserver(applyWCAGHeadings);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [value]);

    const handleHover = (buttonName: string) => {
        setAnnouncement(buttonName);
        setTimeout(() => setAnnouncement(''), 100);
    };

    const handleDateChange = (date: any) => {
        if (onChange) {
            onChange(date);
        }

        // Announce the selected date after selection
        if (date && date instanceof Date && !isNaN(date.getTime())) {
            const selectedDateLabel = date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
            setAnnouncement(`Selected date: ${selectedDateLabel}`);
            setTimeout(() => setAnnouncement(''), 2000);
        }
    };

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            {/* aria-live region for NVDA hover announcements */}
            <div
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                }}
            >
                {announcement}
            </div>
            <KeyboardDatePicker
                allowKeyboardControl={false}
                variant={'dialog'}
                disableFuture={true}
                format={'dd/MM/yyyy'}
                placeholder={'dd/mm/yyyy'}
                id={id}
                label={label}
                value={value}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                // ✅ FIXED accessibility
                renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) => {
                    if (!day || !dayComponent) return dayComponent;

                    const isSelected = selectedDate && day.getTime() === selectedDate.getTime();

                    const isToday = day.getTime() === new Date().setHours(0, 0, 0, 0);

                    const dayLabel = day.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                    });

                    let fullLabel = dayLabel;

                    if (isToday) fullLabel += ', today';
                    if (isSelected) fullLabel += ', selected';

                    return React.cloneElement(dayComponent, {
                        role: 'gridcell',
                        'aria-selected': isSelected ? 'true' : 'false',
                        'aria-current': isToday ? 'date' : undefined,
                        'aria-label': fullLabel, //ensures "selected" is read
                        onMouseEnter: () => {
                            // Announce the date on hover
                            const hoverLabel = day.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            });
                            let announcementText = hoverLabel;
                            if (isToday) announcementText += ', today';
                            if (isSelected) announcementText += ', selected';
                            setAnnouncement(announcementText);
                            setTimeout(() => setAnnouncement(''), 2000);
                        },
                        onFocus: () => {
                            // Announce the date on keyboard focus
                            const focusLabel = day.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            });
                            let announcementText = focusLabel;
                            if (isToday) announcementText += ', today';
                            if (isSelected) announcementText += ', selected';
                            setAnnouncement(announcementText);
                            setTimeout(() => setAnnouncement(''), 2000);
                        },
                        style: {
                            ...dayComponent.props.style,
                            backgroundColor: isSelected ? '#1976d2' : undefined,
                            color: isSelected ? 'white' : undefined,
                        },
                    });
                }}
                // Open picker button
                KeyboardButtonProps={{
                    'aria-label': `Open date picker${label ? ` for ${label}` : ''}`,
                    onMouseEnter: () =>
                        handleHover(`Open date picker${label ? ` for ${label}` : ''}`),
                }}
                // Navigation buttons
                leftArrowButtonProps={{
                    'aria-label': 'Previous month',
                    onMouseEnter: () => handleHover('Previous month'),
                }}
                rightArrowButtonProps={{
                    'aria-label': 'Next month',
                    onMouseEnter: () => handleHover('Next month'),
                }}
                InputProps={{
                    readOnly: true,
                    inputProps: {
                        'aria-label': label ? String(label) : 'Date',
                    },
                }}
                inputVariant={'outlined'}
                {...restWithoutOnChange}
            />
            {/* ✅ HIGH CONTRAST FOCUS FIX */}
            <style>
                {`
                /* DAYS (unselected) — light focus background works because the
                   day number is dark grey/black, so contrast is preserved. */
                .MuiPickersDay-day:not(.MuiPickersDay-daySelected):not(.Mui-selected):not([aria-selected="true"]):focus-visible {
                    outline: 3px solid #0B5FFF !important;
                    outline-offset: 2px !important;
                    background: #E8F0FF !important;
                }

                /* DAYS (selected) - keep the blue fill + white number on focus
                   so the digit stays visible. The earlier rule used
                   .Mui-selected, which is MUI v5 only; this codebase is on
                   @material-ui/pickers v3, which exposes the day as
                   .MuiPickersDay-daySelected. We match both, plus the
                   aria-selected="true" set in renderDay, so the override
                   wins regardless of MUI version. */
                .MuiPickersDay-day.MuiPickersDay-daySelected:focus-visible,
                .MuiPickersDay-day.Mui-selected:focus-visible,
                .MuiPickersDay-day[aria-selected="true"]:focus-visible {
                    background: #1976d2 !important;
                    background-color: #1976d2 !important;
                    color: #ffffff !important;
                    outline: 3px solid #ffffff !important;
                    outline-offset: 2px !important;
                    box-shadow: 0 0 0 6px #0B5FFF !important;
                }
                /* Ensure the inner <p> / typography inside the day button
                   inherits the white color on focus too, in case MUI wraps the
                   number in its own element with a hard-coded color. */
                .MuiPickersDay-day.MuiPickersDay-daySelected:focus-visible *,
                .MuiPickersDay-day.Mui-selected:focus-visible *,
                .MuiPickersDay-day[aria-selected="true"]:focus-visible * {
                    color: #ffffff !important;
                }

                /* HEADER NAV BUTTONS */
                .MuiPickersCalendarHeader-iconButton:focus-visible {
                    outline: 3px solid #0B5FFF !important;
                    outline-offset: 2px !important;
                    background: #E8F0FF !important;
                }

                /* OPEN CALENDAR BUTTON */
                .MuiIconButton-root:focus-visible {
                    outline: 3px solid #0B5FFF !important;
                    outline-offset: 2px !important;
                    background: #E8F0FF !important;
                    border-radius: 4px;
                }

                /* DATEPICKER HEADER BACKGROUND COLOR */
                .MuiPickersToolbar-toolbar {
                    background-color: #7C6BFF !important;
                }

                /* WCAG HEADING HIERARCHY - Year as h1, Month as h2 */
                .MuiPickersToolbar-toolbar .MuiPickersToolbar-yearTitle {
                    color: #000000 !important;
                    font-weight: 600 !important;
                    font-size: 1.5rem !important;
                    /* Make this behave like h1 for screen readers */
                    position: relative;
                }
                .MuiPickersToolbar-toolbar .MuiPickersToolbar-yearTitle::before {
                    content: '';
                    position: absolute;
                    left: -10000px;
                    width: 1px;
                    height: 1px;
                    overflow: hidden;
                }
                .MuiPickersToolbar-toolbar .MuiPickersToolbarText-toolbarTxt {
                    color: #000000 !important;
                    font-weight: 600 !important;
                    font-size: 1.25rem !important;
                    /* Make this behave like h2 for screen readers */
                    position: relative;
                }
                .MuiPickersToolbar-toolbar .MuiPickersToolbarText-toolbarTxt::before {
                    content: '';
                    position: absolute;
                    left: -10000px;
                    width: 1px;
                    height: 1px;
                    overflow: hidden;
                }
                .MuiPickersToolbar-yearSelection {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }
                .MuiPickersToolbar-text {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }
                .MuiPickersToolbar-toolbar .MuiTypography-colorInherit {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }

                /* DAY HEADERS CONTRAST FIX (Su, Mo, Tu, We, Th, Fr, Sa) */
                .MuiPickersCalendarHeader-dayLabel {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }
                .MuiPickersCalendarHeader-weekDayLabel {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }
                .MuiPickersCalendarHeader-transition {
                    color: #000000 !important;
                }
                .MuiPickersCalendarHeader-weekDayLabel.MuiTypography-body1 {
                    color: #000000 !important;
                    font-weight: 600 !important;
                }
                .MuiPickersSlideTransition-root {
                    color: #000000 !important;
                }

                /* REMOVE WEAK RIPPLE VISUAL ONLY */
                .MuiTouchRipple-root {
                    opacity: 0 !important;
                }

                /* HEADING STRUCTURE FIX - Maintain proper styling for modified headings */
                .MuiPickersToolbar-toolbar h2 {
                    font-size: 1.5rem !important;
                    font-weight: 600 !important;
                    color: #000000 !important;
                    margin: 0 !important;
                }

                .MuiPickersToolbar-toolbar h3 {
                    font-size: 1.25rem !important;
                    font-weight: 600 !important;
                    color: #000000 !important;
                    margin: 0 !important;
                }
                `}
            </style>
        </MuiPickersUtilsProvider>
    );
}
